import type {
  DashboardStatus,
  Job,
  JobDraft,
  JobResult,
  SocialAccounts,
  SocialDraft,
  SocialProvider,
  SocialPublish,
  SocialPublishInput,
  TikTokCreatorInfo,
} from "./types";

export class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

async function responseError(response: Response): Promise<ApiError> {
  let message = `요청 실패 (${response.status})`;
  try {
    const data = (await response.json()) as { error?: string };
    if (data.error) message = data.error;
  } catch {
    // Preserve the HTTP fallback when a proxy returns a non-JSON error.
  }
  return new ApiError(message, response.status);
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: "same-origin",
    ...init,
    headers: {
      ...(init?.body instanceof FormData ? {} : { "Content-Type": "application/json" }),
      ...init?.headers,
    },
  });
  if (!response.ok) throw await responseError(response);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

async function uploadReferenceImage(id: string, reference: File): Promise<Job> {
  const form = new FormData();
  form.set("reference_image", reference);
  return request<Job>(`/api/jobs/${id}/reference-images`, { method: "POST", body: form });
}

export const api = {
  session: () => request<{ authenticated: boolean }>("/api/session"),
  login: (key: string) => request<{ authenticated: boolean }>("/api/session", {
    method: "POST",
    body: JSON.stringify({ key }),
  }),
  logout: () => request<{ authenticated: boolean }>("/api/session", { method: "DELETE" }),
  snapshot: () => request<{ jobs: Job[]; status: DashboardStatus }>("/api/snapshot"),
  jobs: () => request<{ jobs: Job[] }>("/api/jobs"),
  status: () => request<DashboardStatus>("/api/status"),
  createJob: (
    draft: JobDraft,
    reference: File,
    status: "draft" | "ready",
    referenceDuration?: number | null,
    referenceImages: File[] = [],
    referenceDimensions?: { width: number; height: number } | null,
  ) => {
    const form = new FormData();
    form.set("reference", reference);
    form.set("name", draft.name);
    form.set("prompt", draft.prompt);
    form.set("profile", draft.profile);
    form.set("width", String(draft.width));
    form.set("height", String(draft.height));
    form.set("frames", String(draft.frames));
    form.set("seed", String(draft.seed));
    form.set("reference_intent", draft.reference_intent);
    form.set("status", referenceImages.length ? "draft" : status);
    if (referenceDuration != null) form.set("reference_duration", String(referenceDuration));
    if (referenceDimensions) {
      form.set("reference_width", String(referenceDimensions.width));
      form.set("reference_height", String(referenceDimensions.height));
    }
    return request<Job>("/api/jobs", { method: "POST", body: form }).then(async (created) => {
      let job = created;
      try {
        for (const image of referenceImages) job = await uploadReferenceImage(job.id, image);
        if (status === "ready" && referenceImages.length) {
          job = await request<Job>(`/api/jobs/${job.id}/ready`, { method: "POST" });
        }
        return job;
      } catch (error) {
        await request<void>(`/api/jobs/${created.id}`, { method: "DELETE" }).catch(() => undefined);
        throw error;
      }
    });
  },
  updateJob: (id: string, draft: JobDraft) => request<Job>(`/api/jobs/${id}`, {
    method: "PATCH",
    body: JSON.stringify(draft),
  }),
  replaceReference: (
    id: string,
    reference: File,
    referenceDuration?: number | null,
    videoGeometry?: {
      referenceWidth: number;
      referenceHeight: number;
      width: number;
      height: number;
    } | null,
  ) => {
    const form = new FormData();
    form.set("reference", reference);
    if (referenceDuration != null) form.set("reference_duration", String(referenceDuration));
    if (videoGeometry) {
      form.set("reference_width", String(videoGeometry.referenceWidth));
      form.set("reference_height", String(videoGeometry.referenceHeight));
      form.set("width", String(videoGeometry.width));
      form.set("height", String(videoGeometry.height));
    }
    return request<Job>(`/api/jobs/${id}/reference`, { method: "PUT", body: form });
  },
  addReferenceImages: async (id: string, references: File[]) => {
    let job: Job | null = null;
    for (const reference of references) job = await uploadReferenceImage(id, reference);
    if (!job) throw new Error("추가할 보조 레퍼런스가 없습니다.");
    return job;
  },
  deleteReferenceImage: (id: string, imageId: string) => request<Job>(
    `/api/jobs/${id}/reference-images/${imageId}`,
    { method: "DELETE" },
  ),
  markReady: (id: string) => request<Job>(`/api/jobs/${id}/ready`, { method: "POST" }),
  reopenJob: (id: string) => request<Job>(`/api/jobs/${id}/reopen`, { method: "POST" }),
  cloneJob: (id: string) => request<Job>(`/api/jobs/${id}/clone`, { method: "POST" }),
  createCutPlan: (
    id: string,
    targetDuration: number,
    options?: {
      title?: string;
      continuity_mode?: "independent" | "previous_result_last_frame";
      shots?: Array<{ name: string; prompt: string; duration_seconds: number; seed: number }>;
    },
  ) => request<{ jobs: Job[]; shot_count: number; group_id: string; continuity_mode: string }>(
    `/api/jobs/${id}/cut-plan`,
    { method: "POST", body: JSON.stringify({ target_duration_seconds: targetDuration, ...options }) },
  ),
  queueProduction: (groupId: string) => request<{ id: string; job_count: number; added_count: number }>(
    `/api/productions/${groupId}/queue`,
    { method: "POST", body: "{}" },
  ),
  jobResults: (id: string) => request<{ results: JobResult[] }>(`/api/jobs/${id}/results`),
  socialAccounts: () => request<SocialAccounts>("/api/social"),
  connectSocial: (provider: SocialProvider) => request<{ url: string }>(`/api/social/connect/${provider}`, {
    method: "POST",
    body: "{}",
  }),
  disconnectSocial: (connectionId: string) => request<void>(`/api/social/connections/${connectionId}`, {
    method: "DELETE",
  }),
  tiktokCreatorInfo: (connectionId: string) => request<TikTokCreatorInfo>(
    `/api/social/connections/${connectionId}/creator-info`,
  ),
  socialDraft: (resultId: string) => request<SocialDraft>(`/api/social/results/${resultId}/draft`, {
    method: "POST",
    body: "{}",
  }),
  publishSocial: (resultId: string, input: SocialPublishInput) => request<SocialPublish>(
    `/api/social/results/${resultId}/publish`,
    { method: "POST", body: JSON.stringify(input) },
  ),
  socialPublishes: (resultId: string) => request<{ publishes: SocialPublish[] }>(
    `/api/social/results/${resultId}/publishes`,
  ),
  retryPostprocess: (id: string, resultId: string) => request<JobResult>(
    `/api/jobs/${id}/results/${resultId}/postprocess`,
    { method: "POST" },
  ),
  requestMotionFix: (id: string, resultId: string, speedFactor: 1.1 | 1.2 | 1.35) => request<JobResult>(
    `/api/jobs/${id}/results/${resultId}/motion-fix`,
    { method: "POST", body: JSON.stringify({ speed_factor: speedFactor }) },
  ),
  retry: (id: string) => request<Job>(`/api/jobs/${id}/retry`, { method: "POST" }),
  rewritePrompt: (id: string, prompt: string) => request<Job>(`/api/jobs/${id}/prompt-rewrite`, {
    method: "POST",
    body: JSON.stringify({ prompt }),
  }),
  translatePrompt: (prompt: string, signal?: AbortSignal) => request<{ translation: string; cached: boolean; model: string; status: "requested" | "processing" | "completed" }>(
    "/api/prompt-translation",
    { method: "POST", body: JSON.stringify({ prompt }), signal },
  ),
  upgradeRealism: (id: string) => request<Job>(`/api/jobs/${id}/realism-upgrade`, {
    method: "POST",
  }),
  upgradeWaitingRealism: () => request<{ requested_count: number; skipped_count: number }>(
    "/api/realism-upgrades",
    { method: "POST" },
  ),
  deleteJob: (id: string) => request<void>(`/api/jobs/${id}`, { method: "DELETE" }),
  runBatch: (provider: "vast" | "windows" = "vast", jobIds?: string[]) => request<{ id: string; status: string; job_count: number; added_count: number; provider?: string }>("/api/batches", {
    method: "POST",
    body: JSON.stringify({ provider, ...(jobIds?.length ? { job_ids: jobIds } : {}) }),
  }),
};
